import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Label } from "../ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { Coins, Loader2, Users, Trash2, Plus, ArrowRight, X, Clock, MapPin, Crosshair, Send, Check, AlertCircle, DollarSign, UserPlus } from "lucide-react";

export default function CreateHunterLFGModal({
  showCreateLfg, setShowCreateLfg, lfgForm, setLfgForm, handleCreateLfg,
  actionLoading, userCharacters, AREA_BOSSES, availableJobs, expeditionBosses, toggleLfgBoss, toggleLfgCharacter
}) {
  return (
      <Dialog open={showCreateLfg} onOpenChange={setShowCreateLfg}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" style={{ background: "linear-gradient(180deg, #1a0d40 0%, #0a0628 100%)", border: "2px solid #7c4dff" }}>
          <DialogHeader>
            <DialogTitle className="text-purple-300 flex items-center gap-2">
              <UserPlus className="w-5 h-5" /> Post Looking for Group
            </DialogTitle>
            <DialogDescription className="text-purple-200/60">
              Let expedition leaders know you're available
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-purple-200 font-semibold mb-2 block">Boss Targets (select all that apply)</Label>
              <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto p-1">
                {expeditionBosses.map((boss) => {
                  const isSelected = lfgForm.boss_ids.includes(boss.id);
                  return (
                    <div
                      key={boss.id}
                      className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer text-sm transition-all ${isSelected ? "ring-1 ring-purple-400 bg-purple-500/20" : "bg-black/20 hover:bg-purple-500/10"}`}
                      onClick={() => toggleLfgBoss(boss.id)}
                      data-testid={`lfg-boss-${boss.id}`}
                    >
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${isSelected ? "bg-purple-500 border-purple-400" : "border-purple-500/40"}`}>
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <span className={`truncate ${isSelected ? "text-purple-200" : "text-gray-400"}`}>{boss.name}</span>
                    </div>
                  );
                })}
              </div>
              {lfgForm.boss_ids.length > 0 && (
                <p className="text-xs text-purple-400 mt-1">{lfgForm.boss_ids.length} boss(es) selected</p>
              )}
            </div>

            <div>
              <Label className="text-purple-200 font-semibold mb-2 block">Select Your Character(s)</Label>
              {userCharacters.length > 0 ? (
                <div className="space-y-2">
                  {userCharacters.map((char) => {
                    const isSelected = lfgForm.selected_characters.some(c => c.character_id === char.id);
                    const charSkills = char.skills || availableJobs[char.job]?.skills || [];
                    return (
                      <div
                        key={char.id}
                        className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${isSelected ? "ring-2 ring-purple-400" : "hover:bg-purple-500/10"}`}
                        style={{ background: isSelected ? "rgba(124,77,255,0.2)" : "rgba(0,0,0,0.3)", border: `1px solid ${isSelected ? "rgba(124,77,255,0.6)" : "rgba(124,77,255,0.2)"}` }}
                        onClick={() => toggleLfgCharacter(char)}
                        data-testid={`lfg-char-${char.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex gap-1">
                            {charSkills.map((skill, si) => (
                              <img key={si} src={skill.icon} alt={skill.name} className="w-6 h-6" title={skill.name} />
                            ))}
                          </div>
                          <div>
                            <p className="text-purple-100 font-medium">{char.name || char.job}</p>
                            <p className="text-xs text-purple-300/70">{char.job} - Lv.{char.level}</p>
                          </div>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isSelected ? "bg-purple-500 border-purple-400" : "border-purple-500/40"}`}>
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-4 text-purple-300/60">
                  <AlertCircle className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-sm">No characters registered. Go to Profile &gt; Characters to add one.</p>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                checked={lfgForm.has_hb}
                onCheckedChange={(c) => setLfgForm({ ...lfgForm, has_hb: c })}
              />
              <Label className="text-purple-200">I can provide Hyper Body</Label>
            </div>

            <div>
              <Label className="text-purple-200">Queue Duration</Label>
              <Select value={String(lfgForm.queue_duration_hours)} onValueChange={(v) => setLfgForm({ ...lfgForm, queue_duration_hours: parseInt(v) })}>
                <SelectTrigger className="bg-black/40 border-purple-500/40 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 hour</SelectItem>
                  <SelectItem value="2">2 hours</SelectItem>
                  <SelectItem value="4">4 hours</SelectItem>
                  <SelectItem value="8">8 hours</SelectItem>
                  <SelectItem value="12">12 hours</SelectItem>
                  <SelectItem value="24">24 hours</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-purple-200">Message (Optional)</Label>
              <Textarea
                placeholder="Anything leaders should know..."
                value={lfgForm.message}
                onChange={(e) => setLfgForm({ ...lfgForm, message: e.target.value })}
                className="bg-black/40 border-purple-500/40 text-white"
                rows={2}
                data-testid="lfg-message-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreateLfg(false)}>Cancel</Button>
            <Button
              className="bg-purple-500 hover:bg-purple-600 text-white"
              onClick={handleCreateLfg}
              disabled={lfgForm.boss_ids.length === 0 || lfgForm.selected_characters.length === 0}
              data-testid="lfg-submit-btn"
            >
              Post LFG
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
  );
}
